import { supabase } from './supabaseClient'

// Utility function for retry mechanism
const withRetry = async (fn, maxRetries = 3) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn()
    } catch (error) {
      console.warn(`Attempt ${i + 1} failed:`, error.message)
      if (i === maxRetries - 1) throw error
      await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)))
    }
  }
}

// Validation helpers
const validateSubscription = (data) => {
  const required = ['id', 'user_id', 'status']
  return data && required.every(field => data[field])
}

const validateUser = (userId) => {
  return userId && typeof userId === 'string' && userId.length > 0
}

// Credit costs for different actions
export const CREDIT_COSTS = {
  chat: 1,
  web_search: 2,
  academic_search: 2,
  // Agent types (matching AGENT_TYPES from agents/index.js)
  skripsi_agent: 100,
  makalah_agent: 30,
  artikel_agent: 7,
  gambar_agent: 5,
  edit_gambar_agent: 15, // Edit Gambar Agent consumes 15 credits per use
  video_agent: 30, // Video Agent consumes 30 credits per generation
  berita_agent: 7,
  buku_agent: 200,
  novel_agent: 200, // Novel Agent consumes 200 credits per use
  search_agent: 5,
  trending_agent: 2,
  writing_agent: 1,
  ppt_agent: 20, // Updated to 20 credits as requested
  chart_agent: 10, // Chart Agent consumes 10 credits per use
  research_agent: 30, // Research Agent consumes 30 credits per use
  pdf_agent: 10, // PDF Agent consumes 10 credits per analysis
  // Writing Agent actions (based on WritingEditorPage.jsx aiActions)
  improve: 1,
  expand: 1,
  shorten: 1,
  rephrase: 1,
  translate: 1,
  grammar: 1
}

export class SubscriptionService {
  // Get all available subscription packages
  static async getPackages() {
    try {
      const { data, error } = await supabase
        .from('subscription_packages')
        .select('*')
        .eq('is_active', true)
        .order('price', { ascending: true })
      
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      console.error('Error fetching packages:', error)
      return { data: null, error }
    }
  }

  // Ensure user has FREE subscription with zero credits if no active subscription
  static async ensureFreeSubscription(userId) {
    try {
      if (!validateUser(userId)) {
        throw new Error('Valid User ID is required')
      }

      console.log(`[ensureFreeSubscription] Checking FREE subscription for user: ${userId}`)
      
      const { data, error } = await supabase
        .rpc('ensure_free_subscription_for_user', {
          user_id_param: userId
        })
      
      if (error) {
        console.error(`[ensureFreeSubscription] Error:`, error)
        throw error
      }
      
      if (data) {
        console.log(`[ensureFreeSubscription] Created FREE subscription for user: ${userId}`)
      } else {
        console.log(`[ensureFreeSubscription] User already has active subscription or FREE subscription: ${userId}`)
      }
      
      return { success: true, created: data }
    } catch (error) {
      console.error(`[ensureFreeSubscription] Error:`, error)
      return { success: false, error: error.message }
    }
  }

  // Get user's current active subscription
  static async getCurrentSubscription(userId) {
    return withRetry(async () => {
      try {
        // Enhanced logging for debugging
        console.log('Fetching subscription for user:', userId)
        
        // Validate user ID
        if (!validateUser(userId)) {
          throw new Error('Valid User ID is required')
        }
      
      // First try with JOIN, fallback to separate queries if needed
      let data, error
      
      try {
        const result = await supabase
          .from('user_subscriptions')
          .select(`
            *,
            subscription_packages(*)
          `)
          .eq('user_id', userId)
          .eq('status', 'active')
          .gt('expires_at', new Date().toISOString())
          .order('created_at', { ascending: false })
          .limit(1)
          .single()
        
        data = result.data
        error = result.error
      } catch (joinError) {
        console.warn('JOIN query failed, trying separate queries:', joinError)
        
        // Fallback: Get subscription first, then package separately
        const { data: subscription, error: subError } = await supabase
          .from('user_subscriptions')
          .select('*')
          .eq('user_id', userId)
          .eq('status', 'active')
          .gt('expires_at', new Date().toISOString())
          .order('created_at', { ascending: false })
          .limit(1)
          .single()
        
        if (subError) {
          if (subError.code === 'PGRST116') {
            console.log('No active subscription found for user (fallback):', userId)
            // Don't automatically create FREE subscription here to avoid affecting other users
            return { data: null, error: null }
          }
          error = subError
        } else if (subscription) {
          // Get package details separately
          const { data: packageData } = await supabase
            .from('subscription_packages')
            .select('*')
            .eq('id', subscription.package_id)
            .single()
          
          data = {
            ...subscription,
            subscription_packages: packageData
          }
        }
      }
      
      if (error) {
        console.error('Supabase error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
          userId: userId
        })
        
        // Handle specific error codes
        if (error.code === 'PGRST116') {
          console.log('No active subscription found for user:', userId)
          // Don't automatically create FREE subscription here to avoid affecting other users
          // Let the calling code decide whether to create a FREE subscription
          return { data: null, error: null }
        }
        
        throw error
      }
      
      // Additional validation for subscription data
      if (data) {
        // Double-check if subscription is actually active
        if (data.status !== 'active' || new Date(data.expires_at) <= new Date()) {
          console.warn('⚠️ [getCurrentSubscription] Subscription found but not active or expired:', {
            userId,
            status: data.status,
            expires_at: data.expires_at,
            current_time: new Date().toISOString()
          })
          return { data: null, error: null }
        }
      }
      
      // Validate retrieved data
       if (data && !validateSubscription(data)) {
         console.warn('⚠️ [getCurrentSubscription] Invalid subscription data structure for user:', userId)
       }
       
       return { data, error: null }
     } catch (error) {
       console.error('Error fetching current subscription:', error)
       throw error // Let withRetry handle the retry logic
     }
   })
  }

  // Get comprehensive subscription details using the new view
  static async getSubscriptionDetails(userId) {
    return withRetry(async () => {
      if (!validateUser(userId)) {
        throw new Error('Invalid user ID provided')
      }

      console.log(`[getSubscriptionDetails] Fetching detailed subscription for user: ${userId}`)
      
      try {
        // Try using the view first
        let { data, error } = await supabase
          .from('subscription_details')
          .select('*')
          .eq('user_id', userId)
          .in('subscription_status', ['active', 'pending'])
          .order('transaction_created_at', { ascending: false })
          .limit(1)
          .single()
        
        // If view doesn't work, fallback to manual JOIN
        if (error && error.code !== 'PGRST116') {
          console.warn(`[getSubscriptionDetails] View query failed, using fallback:`, error.message)
          
          const { data: subscription, error: subError } = await supabase
            .from('user_subscriptions')
            .select(`
              *,
              subscription_packages(*),
              subscription_transactions(*)
            `)
            .eq('user_id', userId)
            .in('status', ['active', 'pending'])
            .order('created_at', { ascending: false })
            .limit(1)
            .single()
          
          if (subError) {
            if (subError.code === 'PGRST116') {
              console.log(`[getSubscriptionDetails] No subscription found for user: ${userId}`)
              return { data: null, error: null }
            }
            error = subError
          } else {
            // Transform to match view structure
            data = {
              subscription_id: subscription.id,
              user_id: subscription.user_id,
              subscription_status: subscription.status,
              credits_remaining: subscription.credits_remaining,
              credits_total: subscription.credits_total,
              started_at: subscription.started_at,
              expires_at: subscription.expires_at,
              package_name: subscription.subscription_packages?.name,
              package_price: subscription.subscription_packages?.price,
              package_credits: subscription.subscription_packages?.credits,
              package_features: subscription.subscription_packages?.features,
              transaction_id: subscription.subscription_transactions?.id,
              order_id: subscription.subscription_transactions?.order_id,
              transaction_amount: subscription.subscription_transactions?.amount,
              transaction_status: subscription.subscription_transactions?.status,
              payment_method: subscription.subscription_transactions?.payment_method,
              transaction_created_at: subscription.subscription_transactions?.created_at
            }
            error = null
          }
        }
        
        if (error) {
          if (error.code === 'PGRST116') {
            console.log(`[getSubscriptionDetails] No subscription details found for user: ${userId}`)
            return { data: null, error: null }
          }
          
          console.error(`[getSubscriptionDetails] Error:`, error)
          throw error
        }
        
        console.log(`[getSubscriptionDetails] Found subscription details:`, data?.subscription_id)
        return { data, error: null }
        
      } catch (error) {
        console.error(`[getSubscriptionDetails] Error:`, error)
        throw error
      }
    })
  }

  // Validate subscription-transaction relationships
  static async validateRelationships() {
    try {
      console.log('[validateRelationships] Checking subscription-transaction relationships')
      
      const { data, error } = await supabase
        .rpc('validate_subscription_transaction_relationship')
      
      if (error) {
        console.error('[validateRelationships] Error:', error)
        throw error
      }
      
      const invalidRelationships = data.filter(item => !item.is_valid)
      
      if (invalidRelationships.length > 0) {
        console.warn(`[validateRelationships] Found ${invalidRelationships.length} invalid relationships:`, invalidRelationships)
      } else {
        console.log('[validateRelationships] All relationships are valid')
      }
      
      return {
        data: {
          total: data.length,
          valid: data.filter(item => item.is_valid).length,
          invalid: invalidRelationships.length,
          invalidRelationships
        },
        error: null
      }
      
    } catch (error) {
      console.error('[validateRelationships] Error:', error)
      return { data: null, error: error.message }
    }
  }

  // Create a new subscription transaction
  static async createTransaction(userId, packageId) {
    try {
      // Generate unique order ID
      const orderId = `ORDER-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`
      
      // Get package details
      const { data: packageData, error: packageError } = await supabase
        .from('subscription_packages')
        .select('*')
        .eq('id', packageId)
        .single()
      
      if (packageError) throw packageError
      
      // Create transaction
      const { data, error } = await supabase
        .from('subscription_transactions')
        .insert({
          user_id: userId,
          package_id: packageId,
          order_id: orderId,
          amount: packageData.price,
          status: 'pending'
        })
        .select('*')
        .single()
      
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      console.error('Error creating transaction:', error)
      return { data: null, error }
    }
  }

  // Get transaction by order ID
  static async getTransaction(orderId) {
    try {
      const { data, error } = await supabase
        .from('subscription_transactions')
        .select(`
          *,
          subscription_packages(*)
        `)
        .eq('order_id', orderId)
        .single()
      
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      console.error('Error fetching transaction:', error)
      return { data: null, error }
    }
  }

  // Check if user has enough credits for an action
  static async checkCredits(userId, actionType) {
    try {
      console.log(`🔍 [checkCredits] Starting credit check for user ${userId}, action: ${actionType}`)
      
      const requiredCredits = CREDIT_COSTS[actionType]
      if (!requiredCredits) {
        throw new Error(`Unknown action type: ${actionType}`)
      }

      const { data: subscription } = await this.getCurrentSubscription(userId)
      
      console.log(`📊 [checkCredits] Subscription data:`, {
        hasSubscription: !!subscription,
        subscriptionId: subscription?.id,
        status: subscription?.status,
        creditsRemaining: subscription?.credits_remaining,
        creditsTotal: subscription?.credits_total,
        packageName: subscription?.subscription_packages?.name,
        expiresAt: subscription?.expires_at
      })
      
      if (!subscription) {
        console.log(`❌ [checkCredits] No subscription found for user ${userId}`)
        return { 
          hasCredits: false, 
          remainingCredits: 0, 
          requiredCredits,
          error: 'No active subscription',
          needsUpgrade: true,
          packageName: null
        }
      }

      const hasCredits = subscription.credits_remaining >= requiredCredits
      const isFreePackage = subscription.subscription_packages?.name === 'FREE'
      
      console.log(`💳 [checkCredits] Credit calculation:`, {
        userId,
        actionType,
        creditsRemaining: subscription.credits_remaining,
        requiredCredits,
        hasCredits,
        packageName: subscription.subscription_packages?.name,
        isFreePackage
      })
      
      // Log when there's a potential issue
      if (!hasCredits) {
        console.warn(`⚠️ [checkCredits] Insufficient credits for ${actionType}:`, {
          userId,
          creditsRemaining: subscription.credits_remaining,
          requiredCredits,
          packageName: subscription.subscription_packages?.name
        })
      } else {
        console.log(`✅ [checkCredits] Sufficient credits for ${actionType}`)
      }
      
      return {
        hasCredits,
        remainingCredits: subscription.credits_remaining,
        requiredCredits,
        packageName: subscription.subscription_packages?.name,
        isFreePackage,
        needsUpgrade: !hasCredits,
        error: null
      }
    } catch (error) {
      console.error('❌ [checkCredits] Error checking credits:', error)
      return { 
        hasCredits: false, 
        remainingCredits: 0, 
        requiredCredits: CREDIT_COSTS[actionType] || 0,
        needsUpgrade: true,
        error 
      }
    }
  }

  // Consume credits for an action
  static async consumeCredits(userId, actionType, metadata = {}) {
    try {
      const requiredCredits = CREDIT_COSTS[actionType]
      if (!requiredCredits) {
        throw new Error(`Unknown action type: ${actionType}`)
      }

      // Get current subscription
      const { data: subscription } = await this.getCurrentSubscription(userId)
      
      if (!subscription) {
        return {
          success: false,
          error: 'No active subscription',
          needsUpgrade: true,
          packageName: null
        }
      }

      if (subscription.credits_remaining < requiredCredits) {
        return {
          success: false,
          error: 'Insufficient credits',
          needsUpgrade: true,
          packageName: subscription.subscription_packages?.name,
          creditsRemaining: subscription.credits_remaining,
          creditsRequired: requiredCredits
        }
      }

      // Update subscription credits
      const newCreditsRemaining = subscription.credits_remaining - requiredCredits
      
      const { error: updateError } = await supabase
        .from('user_subscriptions')
        .update({ credits_remaining: newCreditsRemaining })
        .eq('id', subscription.id)
      
      if (updateError) {
        throw updateError;
      }

      // Log credit usage
      const { error: logError } = await supabase
        .from('credit_usage_log')
        .insert({
          user_id: userId,
          subscription_id: subscription.id,
          action_type: actionType,
          credits_used: requiredCredits,
          credits_remaining: newCreditsRemaining,
          metadata
        })
      
      if (logError) {
        throw logError;
      }

      return {
        success: true,
        creditsUsed: requiredCredits,
        creditsRemaining: newCreditsRemaining,
        packageName: subscription.subscription_packages?.name,
        error: null
      }
    } catch (error) {
      console.error('Error consuming credits:', error)
      
      // Handle different types of errors
      let errorMessage = 'An error occurred while consuming credits'
      
      if (error.message) {
        errorMessage = error.message
      } else if (typeof error === 'string') {
        errorMessage = error
      } else if (error.error_description) {
        errorMessage = error.error_description
      } else if (error.details) {
        errorMessage = error.details
      }
      
      return { 
        success: false, 
        error: errorMessage,
        needsUpgrade: true
      }
    }
  }

  // Check if user has access to specific agent based on subscription package
  static async checkAgentAccess(userId, agentType) {
    try {
      const { data: subscription } = await this.getCurrentSubscription(userId)
      
      if (!subscription) {
        console.log('🔍 [checkAgentAccess] No subscription found for user:', userId)
        return {
          hasAccess: false,
          reason: 'no_subscription',
          needsUpgrade: true,
          message: 'Anda perlu berlangganan untuk menggunakan agent ini'
        }
      }
      
      const packageFeatures = subscription.subscription_packages?.features
      const allowedAgents = packageFeatures?.allowed_agents || []
      const packageName = subscription.subscription_packages?.name
      
      // Map agent types to database values
      const agentMapping = {
        'skripsi_agent': 'skripsi',
        'artikel_agent': 'artikel',
        'berita_agent': 'artikel', // berita uses artikel agent
        'makalah_agent': 'makalah',
        'gambar_agent': 'gambar',
        'video_agent': 'video',
        'buku_agent': 'buku',
        'novel_agent': 'novel',
        'search_agent': 'search',
        'trending_agent': 'trending',
        'writing_agent': 'writing',
        'ppt_agent': 'ppt',
        'chart_agent': 'chart',
        'pdf_agent': 'pdf',
        'research_agent': 'research'
      }
      
      const mappedAgentType = agentMapping[agentType] || agentType
      const hasAccess = allowedAgents.includes(mappedAgentType)
      
      // Debug logging for video_agent specifically
      if (agentType === 'video_agent') {
        console.log('🔍 [SubscriptionService.checkAgentAccess] Video agent access check:', {
          userId,
          agentType,
          mappedAgentType,
          packageName,
          allowedAgents,
          hasAccess,
          subscriptionId: subscription.id,
          subscriptionStatus: subscription.status
        })
      }
      
      if (!hasAccess) {
        return {
          hasAccess: false,
          reason: 'agent_not_allowed',
          needsUpgrade: true,
          packageName,
          message: `Agent ${agentType} tidak tersedia untuk paket ${packageName}. Silakan upgrade paket Anda.`
        }
      }
      
      return {
        hasAccess: true,
        reason: 'access_granted',
        needsUpgrade: false,
        packageName
      }
    } catch (error) {
      console.error('Error checking agent access:', error)
      return {
        hasAccess: false,
        reason: 'error',
        needsUpgrade: true,
        error: error.message
      }
    }
  }

  // Get user's credit usage history
  static async getCreditUsageHistory(userId, limit = 50) {
    try {
      const { data, error } = await supabase
        .from('credit_usage_log')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit)
      
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      console.error('Error fetching credit usage history:', error)
      return { data: null, error }
    }
  }

  // Get user's transaction history
  static async getUserTransactionHistory(userId) {
    try {
      let { data, error } = await supabase
        .from('subscription_transactions')
        .select(`
          *,
          subscription_packages(name, credits)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })

      // If JOIN query fails, fallback to separate queries
      if (error) {
        console.warn('[getUserTransactionHistory] JOIN query failed, using fallback:', error.message)
        
        // Get transactions first
        const { data: transactions, error: transError } = await supabase
          .from('subscription_transactions')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
        
        if (transError) throw transError
        
        // Enrich with package data
        data = await Promise.all(
          (transactions || []).map(async (transaction) => {
            try {
              const { data: packageData } = await supabase
                .from('subscription_packages')
                .select('name, credits')
                .eq('id', transaction.package_id)
                .single()
              
              return {
                ...transaction,
                subscription_packages: packageData
              }
            } catch {
              return {
                ...transaction,
                subscription_packages: null
              }
            }
          })
        )
      }

      return {
        success: true,
        data: data || []
      }
    } catch (error) {
      console.error('Error fetching user transaction history:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  // Admin methods for managing subscriptions
  static async getAllTransactions(status = 'all') {
    try {
      // Try using the admin_transaction_details view first
      let { data, error } = await supabase
        .from('admin_transaction_details')
        .select('*')
        .order('created_at', { ascending: false })

      // If view query fails, fallback to manual approach
      if (error) {
        console.warn('[getAllTransactions] View query failed, using fallback:', error.message)
        
        let query = supabase
          .from('subscription_transactions')
          .select(`
            *,
            subscription_packages(name, credits),
            user_subscriptions(status, expires_at)
          `)
          .order('created_at', { ascending: false })

        let { data: fallbackData, error: fallbackError } = await query

        // If JOIN query also fails, use separate queries
        if (fallbackError) {
          console.warn('[getAllTransactions] JOIN query failed, using separate queries:', fallbackError.message)
          
          // Get transactions first
          const { data: transactions, error: transError } = await supabase
            .from('subscription_transactions')
            .select('*')
            .order('created_at', { ascending: false })
          
          if (transError) throw transError
          
          // Enrich with package and subscription data
          fallbackData = await Promise.all(
            (transactions || []).map(async (transaction) => {
              try {
                // Get package info
                const { data: packageData } = await supabase
                  .from('subscription_packages')
                  .select('name, credits')
                  .eq('id', transaction.package_id)
                  .single()
                
                // Get subscription info
                const { data: subscriptionData } = await supabase
                  .from('user_subscriptions')
                  .select('status, expires_at')
                  .eq('transaction_id', transaction.id)
                  .single()
                
                // Get user email using RPC
                let userEmail = 'Unknown'
                try {
                  const { data: emailData } = await supabase.rpc('get_user_email', {
                    user_uuid: transaction.user_id
                  })
                  userEmail = emailData || 'Unknown'
                } catch {
                  userEmail = 'Unknown'
                }
                
                return {
                  ...transaction,
                  subscription_packages: packageData,
                  user_subscriptions: subscriptionData,
                  user_email: userEmail,
                  package_name: packageData?.name || 'Unknown',
                  credits: packageData?.credits || 0
                }
              } catch {
                return {
                  ...transaction,
                  subscription_packages: null,
                  user_subscriptions: null,
                  user_email: 'Unknown',
                  package_name: 'Unknown',
                  credits: 0
                }
              }
            })
          )
        } else {
          // Add user email information to fallback data
          fallbackData = await Promise.all(
            (fallbackData || []).map(async (transaction) => {
              try {
                let userEmail = 'Unknown'
                try {
                  const { data: emailData } = await supabase.rpc('get_user_email', {
                    user_uuid: transaction.user_id
                  })
                  userEmail = emailData || 'Unknown'
                } catch {
                  userEmail = 'Unknown'
                }
                
                return {
                  ...transaction,
                  user_email: userEmail,
                  package_name: transaction.subscription_packages?.name || 'Unknown',
                  credits: transaction.subscription_packages?.credits || 0
                }
              } catch {
                return {
                  ...transaction,
                  user_email: 'Unknown',
                  package_name: transaction.subscription_packages?.name || 'Unknown',
                  credits: transaction.subscription_packages?.credits || 0
                }
              }
            })
          )
        }
        
        data = fallbackData
      }

      // Apply status filter if needed
       if (status !== 'all' && data) {
         data = data.filter(transaction => {
           // Helper function to get actual subscription status (same logic as AdminDashboard)
           const getActualStatus = (transaction) => {
             if (transaction.user_subscriptions?.status) {
               return transaction.user_subscriptions.status
             } else if (transaction.subscription_status) {
               return transaction.subscription_status
             } else if (transaction.status === 'success') {
               return 'active'
             } else if (transaction.status === 'cancelled') {
               return 'cancelled'
             }
             return 'pending'
           }
           
           const actualStatus = getActualStatus(transaction)
           
           if (status === 'pending') {
             return actualStatus === 'pending'
           } else if (status === 'active') {
             return actualStatus === 'active'
           } else if (status === 'expired') {
             return actualStatus === 'expired'
           } else if (status === 'cancelled') {
             return actualStatus === 'cancelled'
           }
           return true
         })
       }

      // Ensure consistent data structure
      const normalizedData = (data || []).map(transaction => ({
        ...transaction,
        user_email: transaction.user_email || 'Unknown',
        package_name: transaction.package_name || transaction.subscription_packages?.name || 'Unknown',
        credits: transaction.package_credits || transaction.subscription_packages?.credits || 0
      }))

      return {
        success: true,
        data: normalizedData
      }
    } catch (error) {
      console.error('Error fetching all transactions:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  static async activateSubscription(transactionId) {
    try {
      console.log(`[activateSubscription] Starting activation for transaction: ${transactionId}`)
      console.log(`[activateSubscription] Transaction ID type: ${typeof transactionId}, value: ${JSON.stringify(transactionId)}`)
      
      // Get transaction details using admin view to bypass RLS
      console.log(`[activateSubscription] Querying transaction with ID: ${transactionId}`)
      let { data: transaction, error: transactionError } = await supabase
        .from('admin_transaction_details')
        .select('*')
        .eq('id', transactionId)
        .maybeSingle()
      
      console.log(`[activateSubscription] Initial query result:`, { transaction, transactionError })
      
      // Transform the data to match expected structure
      if (transaction && !transactionError) {
        transaction = {
          ...transaction,
          subscription_packages: {
            id: transaction.package_id,
            name: transaction.package_name,
            credits: transaction.package_credits,
            price: transaction.package_price
          }
        }
      }

      // If JOIN fails, try separate queries
      if (transactionError) {
        console.warn('[activateSubscription] JOIN query failed, using fallback:', transactionError.message)
        
        console.log(`[activateSubscription] Fallback: querying transaction table directly`)
        const { data: transData, error: transError } = await supabase
          .from('subscription_transactions')
          .select('*')
          .eq('id', transactionId)
          .maybeSingle()
        
        console.log(`[activateSubscription] Fallback transaction query result:`, { transData, transError })
        
        if (transError) {
          console.error('[activateSubscription] Error fetching transaction:', transError)
          throw new Error(`Transaction not found: ${transError.message}`)
        }
        
        if (!transData) {
          throw new Error('Transaction not found')
        }
        
        const { data: packageData, error: packageError } = await supabase
          .from('subscription_packages')
          .select('*')
          .eq('id', transData.package_id)
          .maybeSingle()
        
        if (packageError) {
          console.error('[activateSubscription] Error fetching package:', packageError)
          throw new Error(`Package not found: ${packageError.message}`)
        }
        
        if (!packageData) {
          throw new Error('Package not found')
        }
        
        transaction = {
          ...transData,
          subscription_packages: packageData
        }
      }

      if (!transaction) throw new Error('Transaction not found')
      if (transaction.status !== 'pending') {
        console.log(`[activateSubscription] Transaction status is ${transaction.status}, not pending`)
        throw new Error(`Transaction is not pending (current status: ${transaction.status})`)
      }

      console.log(`[activateSubscription] Transaction found, proceeding with activation`)
      
      const now = new Date()
      const expiresAt = new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000)) // 30 days from now

      // Check if user already has an active subscription
      const { data: existingSubscription, error: existingSubError } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', transaction.user_id)
        .eq('status', 'active')
        .maybeSingle()

      // Handle potential errors from checking existing subscription
      if (existingSubError && existingSubError.code !== 'PGRST116') {
        console.error('[activateSubscription] Error checking existing subscription:', existingSubError)
        throw existingSubError
      }

      if (existingSubscription) {
        console.log(`[activateSubscription] Updating existing subscription: ${existingSubscription.id}`)
        // Update existing subscription
        const { error: updateError } = await supabase
          .from('user_subscriptions')
          .update({
            package_id: transaction.package_id,
            transaction_id: transactionId,
            credits_remaining: transaction.subscription_packages.credits,
            credits_total: transaction.subscription_packages.credits,
            expires_at: expiresAt.toISOString(),
            updated_at: now.toISOString()
          })
          .eq('id', existingSubscription.id)

        if (updateError) {
          console.error('[activateSubscription] Error updating subscription:', updateError)
          throw updateError
        }
      } else {
        console.log(`[activateSubscription] Creating new subscription for user: ${transaction.user_id}`)
        // Create new subscription
        const { error: insertError } = await supabase
          .from('user_subscriptions')
          .insert({
            user_id: transaction.user_id,
            package_id: transaction.package_id,
            transaction_id: transactionId,
            credits_remaining: transaction.subscription_packages.credits,
            credits_total: transaction.subscription_packages.credits,
            status: 'active',
            started_at: now.toISOString(),
            expires_at: expiresAt.toISOString()
          })

        if (insertError) {
          console.error('[activateSubscription] Error creating subscription:', insertError)
          throw insertError
        }
      }

      // Update transaction status
      console.log(`[activateSubscription] Updating transaction status to completed`)
      const { error: statusError } = await supabase
        .from('subscription_transactions')
        .update({
          status: 'success',
          updated_at: now.toISOString()
        })
        .eq('id', transactionId)

      if (statusError) {
        console.error('[activateSubscription] Error updating transaction status:', statusError)
        throw statusError
      }

      console.log(`[activateSubscription] Subscription activated successfully`)
      return {
        success: true,
        message: 'Subscription activated successfully'
      }
    } catch (error) {
      console.error('[activateSubscription] Error:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  static async cancelSubscription(transactionId) {
    try {
      console.log(`[cancelSubscription] Starting cancellation for transaction: ${transactionId}`)
      
      // Get transaction details first
      let { data: transaction, error: transError } = await supabase
        .from('subscription_transactions')
        .select(`
          *,
          subscription_packages(name, credits)
        `)
        .eq('id', transactionId)
        .maybeSingle()

      // If JOIN query fails, try separate queries
      if (transError) {
        console.warn('[cancelSubscription] JOIN query failed, using fallback:', transError.message)
        
        const { data: transData, error: transDataError } = await supabase
          .from('subscription_transactions')
          .select('*')
          .eq('id', transactionId)
          .maybeSingle()
        
        if (transDataError) {
          console.error('[cancelSubscription] Error fetching transaction:', transDataError)
          throw new Error(`Transaction not found: ${transDataError.message}`)
        }
        
        if (!transData) {
          throw new Error('Transaction not found')
        }
        
        const { data: packageData, error: packageError } = await supabase
          .from('subscription_packages')
          .select('name, credits')
          .eq('id', transData.package_id)
          .maybeSingle()
        
        if (packageError) {
          console.error('[cancelSubscription] Error fetching package:', packageError)
          throw new Error(`Package not found: ${packageError.message}`)
        }
        
        if (!packageData) {
          throw new Error('Package not found')
        }
        
        transaction = {
          ...transData,
          subscription_packages: packageData
        }
      }

      if (!transaction) throw new Error('Transaction not found')
      
      console.log(`[cancelSubscription] Transaction found, proceeding with cancellation`)
      
      const now = new Date()

      // Find and cancel the active subscription
      const { data: activeSubscription, error: subError } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('transaction_id', transactionId)
        .eq('status', 'active')
        .maybeSingle()

      if (subError && subError.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('[cancelSubscription] Error finding subscription:', subError)
        throw subError
      }

      if (activeSubscription) {
        console.log(`[cancelSubscription] Canceling subscription: ${activeSubscription.id}`)
        // Update subscription status to expired
        const { error: updateError } = await supabase
          .from('user_subscriptions')
          .update({
            status: 'expired',
            expires_at: now.toISOString(),
            updated_at: now.toISOString()
          })
          .eq('id', activeSubscription.id)

        if (updateError) {
          console.error('[cancelSubscription] Error updating subscription:', updateError)
          throw updateError
        }
      } else {
        console.log('[cancelSubscription] No active subscription found for this transaction')
      }

      // Update transaction status to cancelled
      console.log(`[cancelSubscription] Updating transaction status to cancelled`)
      const { error: statusError } = await supabase
        .from('subscription_transactions')
        .update({
          status: 'cancelled',
          updated_at: now.toISOString()
        })
        .eq('id', transactionId)

      if (statusError) {
        console.error('[cancelSubscription] Error updating transaction status:', statusError)
        throw statusError
      }

      console.log(`[cancelSubscription] Subscription cancelled successfully`)
      return {
        success: true,
        message: 'Subscription cancelled successfully'
      }
    } catch (error) {
      console.error('[cancelSubscription] Error:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  // Admin function to manually update user credits
  static async updateUserCredits(userId, newCredits) {
    try {
      console.log(`[updateUserCredits] Updating credits for user: ${userId} to ${newCredits}`)
      
      if (!validateUser(userId)) {
        throw new Error('Valid User ID is required')
      }
      
      if (typeof newCredits !== 'number' || newCredits < 0) {
        throw new Error('Credits must be a non-negative number')
      }
      
      // Get the most recent subscription for the user (regardless of status)
      // This allows editing credits for active, expired, or cancelled subscriptions
      const { data: subscription, error: fetchError } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single()
      
      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('[updateUserCredits] Fetch error:', fetchError)
        throw fetchError
      }
      
      if (!subscription) {
        throw new Error('No subscription found for user')
      }
      
      // Update credits without changing package or expiry date
      const { error: updateError } = await supabase
        .from('user_subscriptions')
        .update({
          credits_remaining: newCredits,
          updated_at: new Date().toISOString()
        })
        .eq('id', subscription.id)
      
      if (updateError) {
        console.error('[updateUserCredits] Update error:', updateError)
        throw updateError
      }
      
      console.log(`[updateUserCredits] Successfully updated credits for user: ${userId}`)
      
      return {
        success: true,
        message: 'Credits updated successfully',
        newCredits
      }
      
    } catch (error) {
      console.error('[updateUserCredits] Error:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }
}

export default SubscriptionService